/**
 * @desc        Livestock2 API Redux
 * @version     1.0.3 (Added support for demo data via override) - 13 May 2026
 */

import { createSlice, createAsyncThunk, configureStore } from '@reduxjs/toolkit'
import Axios from 'axios'
import { FARMDECK_API, TIME_LIMIT_FOR_FETCH } from 'app-config/index.js'
import { accountId } from 'helpers/account.js'
import fastCompare from 'react-fast-compare'
import { format } from 'date-fns'
import { useDispatch, useSelector } from 'react-redux'
import { useMemo, useRef, useCallback, useEffect } from 'react'
import { formatLivestockAge } from 'features-v2/animal-management/components/helpers/dates-general.js'
import { getNestedValue } from 'helper-functions/lodash/lodash-subs.js'
import { usePaddockMap } from 'api/paddocks.js'
import { store } from 'redux/store.js'
import { filterGroups } from 'helper-functions/events/helpers/utils-livestock.js'
import { reverseFormatAnimalIds, extractIndividualAnimals } from 'features-v2/animal-management/components/helpers/animal-ids-utils.js'
// --- helpers ---
const baseUrl = tenant => FARMDECK_API.replace(':id', tenant ?? accountId).replace('/v1/', '/v2/livestock')

const shouldRefetch = (lastFetch, force, cache) => {
    if (force) return true
    if (!cache) return true
    const age = Date.now() - (lastFetch || 0)
    const ttl = typeof TIME_LIMIT_FOR_FETCH === 'number' && TIME_LIMIT_FOR_FETCH > 0 ? TIME_LIMIT_FOR_FETCH : 5 * 60 * 1000
    return age >= ttl
}

// --------------------- Thunks (CRUD-style names) ---------------------
export const fetchCohorts = createAsyncThunk('livestock2/cohorts', async ({ force = false, cache = false, tenant } = {}, { getState, rejectWithValue }) => {
    const { lastFetch, data } = getState().livestock2.cohorts
    if (!data?.items || shouldRefetch(lastFetch, force, cache)) {
        try {
            const url = `${baseUrl(tenant)}/cohorts`
            const res = await Axios.get(url)
            return res.data
        } catch (err) {
            return rejectWithValue(err?.response?.data || err.message || 'fetchCohorts failed')
        }
    }
    return { cached: true, ...(data || {}) }
})

export const createCohort = createAsyncThunk('livestock2/createCohort', async ({ data, tenant } = {}, { rejectWithValue }) => {
    try {
        const url = `${baseUrl(tenant)}/cohorts`
        const res = await Axios.post(url, data)
        return res.data
    } catch (err) {
        return rejectWithValue(err?.response?.data || err.message || 'createCohort failed')
    }
})

export const deleteAnimals = createAsyncThunk('livestock2/deleteAnimals', async ({ ids, tenant } = {}, { rejectWithValue }) => {
    try {
        // console.log('deleteAnimals', ids)
        const url = `${baseUrl(tenant)}/cohorts?ids=${encodeURIComponent(JSON.stringify(ids))}`
        const res = await Axios.delete(url)
        return { ...res.data, ids }
    } catch (err) {
        return rejectWithValue(err?.response?.data || err.message || 'deleteAnimals failed')
    }
})

// This is an alias to "patchAnimals"
export const updateAnimals = createAsyncThunk('livestock2/updateAnimals', async ({ data, ids, tenant, groupId } = {}, { rejectWithValue, dispatch }) => {
    try {
        const url = `${baseUrl(tenant)}/cohorts`
        const res = await Axios.patch(url, data)
        // on success, fetch groups //
        if (res.status === 200) {
            // add a 1 second delay before fetching groups
            setTimeout(() => {
                dispatch(fetchGroups({ tenant, force: true }))
            }, 1000)
        }
        return { response: res.data, ids: ids ?? data?.ids, groupId }
    } catch (err) {
        return rejectWithValue(err?.response?.data || err.message || 'updateAnimals failed')
    }
})

export const updateGroups = createAsyncThunk('livestock2/updateGroups', async ({ operations = [], tenant, isTransact = true, refreshSilent = false } = {}, { rejectWithValue, dispatch }) => {
    try {
        const url = `${baseUrl(tenant)}${isTransact ? '' : '/groups'}`
        const res = await Axios.patch(url, { operations: operations })
        //on success, fetch groups as well
        if (res.status === 200) {
            dispatch(fetchGroups({ tenant, force: true, silent: refreshSilent }))
        }
        return { response: res.data, operations }
    } catch (err) {
        return rejectWithValue({ ok: false, error: { error: err?.response?.data || err?.message || 'updateGroups failed' } })
    }
})

export const transactLivestock = async ({ operations = [], tenant, transactionKey } = {}) => {
    const res = await Axios.patch(baseUrl(tenant), {
        ...(transactionKey ? { transactionKey } : {}),
        operations,
    })
    return res.data
}

export const fetchGroups = createAsyncThunk(
    'livestock2/groups',
    async ({ tenant } = {}, { rejectWithValue }) => {
        try {
            const url = `${baseUrl(tenant)}/groups`
            const res = await Axios.get(url)
            return res.data // expects { items: [...] }
        } catch (err) {
            return rejectWithValue(err?.response?.data || err.message || 'fetchGroups failed')
        }
    },
    {
        condition: ({ force = false, cache = true, tenant } = {}, { getState }) => {
            const { lastFetch, data, loading } = getState().livestock2.groups

            if (force) return true
            if (loading) return false // avoid dupes when not forcing
            if (!cache) return true

            const hasData = Array.isArray(data) && data.length > 0
            const stale = shouldRefetch(lastFetch, false, true)

            return !hasData || stale
        },
    },
)

export const fetchAnimal = createAsyncThunk('livestock2/fetchAnimal', async ({ id }, { getState, dispatch, rejectWithValue }) => {
    const { lastFetch, data } = getState().livestock2.cohorts
    if (lastFetch === 0) {
        try {
            await dispatch(fetchCohorts()).unwrap()
        } catch (err) {
            return rejectWithValue(err?.response?.data ?? err?.message ?? err ?? 'fetchCohorts failed')
        }
    }
    const dataAfter = getState().livestock2.cohorts.data
    const numId = Number(id)
    if (!Number.isFinite(numId)) return rejectWithValue('Invalid animal id')
    const cohort = (dataAfter ?? []).find(c => (c?.aIds ?? []).includes(numId))
    if (!cohort) return rejectWithValue('Animal not found')
    return transformCohortToSingleAnimal(cohort, id)
})

export const createGroup = createAsyncThunk('livestock2/createGroup', async ({ data, tenant } = {}, { rejectWithValue }) => {
    try {
        const url = `${baseUrl(tenant)}/groups`
        const res = await Axios.post(url, data)
        return res.data
    } catch (err) {
        return rejectWithValue(err?.response?.data || err.message || 'createGroup failed')
    }
})

export const updateGroup = createAsyncThunk('livestock2/updateGroup', async ({ data, tenant } = {}, { rejectWithValue }) => {
    try {
        const url = `${baseUrl(tenant)}/groups/${data.id}`
        const res = await Axios.put(url, data)
        return res.data
    } catch (err) {
        return rejectWithValue(err?.response?.data || err.message || 'updateGroup failed')
    }
})

export const deleteGroup = createAsyncThunk('livestock2/deleteGroup', async ({ id, tenant } = {}, { rejectWithValue }) => {
    try {
        const url = `${baseUrl(tenant)}/groups/${id}`
        const res = await Axios.delete(url)
        return { ...res.data, id }
    } catch (err) {
        return rejectWithValue(err?.response?.data || err.message || 'deleteGroup failed')
    }
})

// --------------------- Slice ---------------------
const leaf = () => ({
    lastFetch: 0,
    loading: false,
    data: [],
    error: null,
    lastError: 0,
})

const initialState = {
    cohorts: { ...leaf(), tenantId: '' },
    groups: leaf(),
}

const livestock2Slice = createSlice({
    name: 'livestock2',
    initialState,
    reducers: {},
    extraReducers: builder => {
        // fetchCohorts
        builder.addCase(fetchCohorts.pending, state => {
            state.cohorts.loading = true
        })
        builder.addCase(fetchCohorts.fulfilled, (state, action) => {
            state.cohorts.loading = false
            state.cohorts.data = normalizeCohortIds(action.payload?.items || [])
            state.cohorts.lastFetch = Date.now()
            state.cohorts.error = null
            state.cohorts.tenantId = action.meta.arg?.tenant || accountId || ''
        })
        builder.addCase(fetchCohorts.rejected, (state, action) => {
            state.cohorts.loading = false
            state.cohorts.error = action.payload || action.error?.message || 'fetchCohorts error'
            state.cohorts.lastError = Date.now()
        })
        // createCohort
        builder.addCase(createCohort.pending, state => {
            state.cohorts.loading = true
        })
        builder.addCase(createCohort.fulfilled, (state, action) => {
            state.cohorts.loading = false
            state.cohorts.data = [...state.cohorts.data, ...normalizeCohortIds(action.payload?.items || [])]
            state.cohorts.lastFetch = Date.now()
            state.cohorts.error = null
        })
        builder.addCase(createCohort.rejected, (state, action) => {
            state.cohorts.loading = false
            state.cohorts.error = action.payload || action.error?.message || 'createCohort error'
            state.cohorts.lastError = Date.now()
        })

        // deleteAnimals
        builder.addCase(deleteAnimals.pending, state => {
            state.cohorts.loading = true
        })
        builder.addCase(deleteAnimals.fulfilled, (state, action) => {
            state.cohorts.loading = false
            state.cohorts.data = state.cohorts.data.filter(cohort => !fastCompare(cohort?.ids, action?.payload?.ids))
            state.cohorts.lastFetch = Date.now()
            state.cohorts.error = null
        })
        builder.addCase(deleteAnimals.rejected, (state, action) => {
            state.cohorts.loading = false
            state.cohorts.error = action.payload || action.error?.message || 'deleteAnimals error'
            state.cohorts.lastError = Date.now()
        })

        // updateAnimals
        builder.addCase(updateAnimals.pending, state => {
            state.cohorts.loading = true
        })
        builder.addCase(updateAnimals.fulfilled, (state, action) => {
            state.cohorts.loading = false
            const ids = action?.payload?.ids
            const raw = action?.payload?.response
            const responseItems = Array.isArray(raw) ? raw : (raw?.items ?? [])
            // Remove the original cohort(s) so the backend result replaces them.
            // A split/partial move sends only a SUBSET of a cohort's ids, so an
            // exact fastCompare(cohort.ids, ids) never matches the original and it
            // survives — leaving the moved animals in two cohorts (ghost marker).
            // Match by id overlap instead (ids are unique per animal, so this only
            // ever removes the cohort(s) the moved animals actually came from).
            if (ids != null) {
                const movedIdSet = new Set(reverseFormatAnimalIds(ids))
                state.cohorts.data = movedIdSet.size ? state.cohorts.data.filter(cohort => !reverseFormatAnimalIds(cohort?.ids).some(id => movedIdSet.has(id))) : state.cohorts.data.filter(cohort => !fastCompare(cohort?.ids, ids))
            }
            // wont work
            // if (action?.payload?.groupId) {
            //     console.log('action.payload.groupId', action.payload.groupId, responseItems)
            //     const foundGroup = [...state.groups.data].find(group => group.id === action.payload.groupId)
            //     console.log('foundGroup', foundGroup)
            //     // state.groups.data = state.groups.data.map(group => (group.id === action.payload.groupId ? normalizeOneGroup(action.payload) : group))
            // }
            state.cohorts.data = [...state.cohorts.data, ...normalizeCohortIds(responseItems)]
            state.cohorts.lastFetch = Date.now()
            state.cohorts.error = null
        })
        builder.addCase(updateAnimals.rejected, (state, action) => {
            state.cohorts.loading = false
            state.cohorts.error = action.payload || action.error?.message || 'updateAnimals error'
            state.cohorts.lastError = Date.now()
        })

        // fetchGroups
        builder.addCase(fetchGroups.pending, (state, action) => {
            if (!action.meta?.arg?.silent) state.groups.loading = true
        })
        builder.addCase(fetchGroups.fulfilled, (state, action) => {
            state.groups.loading = false
            state.groups.data = [...normalizeGroupAnimalIds(action.payload?.items || [])]
            state.groups.lastFetch = Date.now()
            state.groups.error = null
        })
        builder.addCase(fetchGroups.rejected, (state, action) => {
            state.groups.loading = false
            state.groups.error = action.payload || action.error?.message || 'fetchGroups error'
            state.groups.lastError = Date.now()
        })

        // createGroup
        builder.addCase(createGroup.pending, state => {
            state.groups.loading = true
        })
        builder.addCase(createGroup.fulfilled, (state, action) => {
            state.groups.loading = false
            state.groups.data = [...state.groups.data, normalizeOneGroup(action.payload)]
            state.groups.lastFetch = Date.now()
            state.groups.error = null
        })
        builder.addCase(createGroup.rejected, (state, action) => {
            state.groups.loading = false
            state.groups.error = action.payload || action.error?.message || 'createGroup error'
            state.groups.lastError = Date.now()
        })

        // updateGroup
        builder.addCase(updateGroup.pending, state => {
            state.groups.loading = true
        })
        builder.addCase(updateGroup.fulfilled, (state, action) => {
            state.groups.loading = false
            state.groups.data = state.groups.data.map(group => (group.id === action.payload?.id ? normalizeOneGroup(action.payload) : group))
            state.groups.lastFetch = Date.now()
            state.groups.error = null
        })
        builder.addCase(updateGroup.rejected, (state, action) => {
            state.groups.loading = false
            state.groups.error = action.payload || action.error?.message || 'updateGroup error'
            state.groups.lastError = Date.now()
        })

        // updateGroup
        builder.addCase(updateGroups.pending, (state, action) => {
            if (!action.meta?.arg?.refreshSilent) state.groups.loading = true
        })
        builder.addCase(updateGroups.fulfilled, (state, action) => {
            state.groups.loading = false
            const operations = action.payload?.operations || []
            const responseItems = action.payload?.response?.items || []
            const groupIds = state.groups.data.map(g => g.id)
            const groupsToUpdate = responseItems.filter(g => groupIds.includes(g.id))
            const groupsToCreate = responseItems.filter(g => !groupIds.includes(g.id))
            // console.log('operations', operations)
            // console.log('responseItems', responseItems)
            // console.log('groupsToUpdate', groupsToUpdate)
            // console.log('groupsToCreate', groupsToCreate)
            // for (const operation of operations) {
            //     if (operation.op === 'delete') {
            //         console.log('deleting group', operation.id)
            //         // for each one of the group in groupsToDelete, delete the group in the state.groups.data array
            //         state.groups.data = state.groups.data.filter(g => g.id === operation.id)
            //     } else if (operation.op === 'update') {
            //         // for each one of the group in groupsToUpdate, update the group in the state.groups.data array
            //         for (const group of groupsToUpdate) {
            //             console.log('updating group', group)
            //             state.groups.data = state.groups.data.map(g => (g.id === group.id ? normalizeOneGroup(group) : g))
            //         }
            //     } else if (operation.op === 'create') {
            //         // for each one of the group in groupsToCreate, create the group in the state.groups.data array
            //         for (const group of groupsToCreate) {
            //             console.log('creating group', group)
            //             state.groups.data = [...state.groups.data, normalizeOneGroup(group)]
            //         }
            //     }
            // }
            state.groups.lastFetch = Date.now()
            state.groups.error = null
        })
        builder.addCase(updateGroups.rejected, (state, action) => {
            state.groups.loading = false
            state.groups.error = action.payload || action.error?.message || 'updateGroup error'
            state.groups.lastError = Date.now()
        })

        // deleteGroup
        builder.addCase(deleteGroup.pending, state => {
            state.groups.loading = true
        })
        builder.addCase(deleteGroup.fulfilled, (state, action) => {
            state.groups.loading = false
            state.groups.data = state.groups.data.filter(group => group.id !== action?.payload?.id)
            state.groups.lastFetch = Date.now()
            state.groups.error = null
        })
        builder.addCase(deleteGroup.rejected, (state, action) => {
            state.groups.loading = false
            state.groups.error = action.payload || action.error?.message || 'deleteGroup error'
            state.groups.lastError = Date.now()
        })
    },
})

export default livestock2Slice.reducer

function normalizeOneGroup(group) {
    return { ...group, aIds: reverseFormatAnimalIds(group?.animalIds) }
}

function fixDateOfBirth(animal) {
    const dateOfBirth = animal?.detail?.birth?.dateOfBirth
    if (!dateOfBirth) return dateOfBirth ?? null
    const [year, month, day] = dateOfBirth.split('-').map(Number)
    if (year < 100) {
        return `20${year}-${month}-${day}`
    }
    return dateOfBirth
}

function normalizeGroupAnimalIds(groups) {
    if (!Array.isArray(groups)) return []
    return groups
        .map(group => {
            const animals =
                group?.animals?.length > 0
                    ? group?.animals?.map(animal => ({
                          ...animal,
                          detail: {
                              ...animal?.detail,
                              birth: {
                                  ...animal?.detail?.birth,
                                  dateOfBirth: fixDateOfBirth(animal),
                              },
                          },
                      }))
                    : []
            if (group?.size === 0) return null
            if (animals.length === 0) return null
            return {
                ...group,
                aIds: reverseFormatAnimalIds(group?.animalIds),
                animals,
                detail: {
                    ...group?.detail,
                    summary: summariseGroupAnimals(group, { fields: ['breeds', 'ageClasses', 'genders', 'healthStatuses', 'paddockIds', 'dobRange', 'dobRangeAll', 'dateOfBirths', 'reproductiveStates', 'dobs', 'reproductiveAvailabilities'] }),
                },
            }
        })
        .filter(Boolean)
}

// Cohorts lack a stable id; we derive one from a hash of animal ids for UI/DB identity.
function normalizeCohortIds(cohorts) {
    if (!Array.isArray(cohorts)) return []
    const safeYear = d => {
        try {
            return format(d, 'yyyy|MM')
        } catch {
            const y = new Date(d).getFullYear()
            return Number.isFinite(y) ? String(y) : '????'
        }
    }
    return cohorts.map(cohort => {
        try {
            const detail = cohort?.detail ?? {}
            const birth = detail?.birth ?? {}
            const extractedIds = [...reverseFormatAnimalIds(cohort?.ids)].sort((a, b) => a - b)
            const size = cohort?.size ?? 0
            const breed = birth?.breed ?? detail?.breed ?? 'Unknown'
            const ageClass = birth?.ageClass ?? 'Weaner'
            const birthDate = birth?.dateOfBirth ?? Date.now()
            const name = `${safeYear(birthDate)} ${breed} ${ageClass}${size > 1 ? 's' : ''}`
            return { ...cohort, uid: cohortIdHex(extractedIds), aIds: extractedIds, name }
        } catch {
            return { ...cohort, uid: '', aIds: [], name: 'Unknown' }
        }
    })
}

/** Pure lookup: group id → group. Use for event classes / non-hook code. Uses == so string/number id match. */
export function getGroupById(groups, id) {
    if (!Array.isArray(groups) || id == null) return null
    return groups.find(g => g?.id == id) ?? null
}

/** Returns a single normalized cohort (with uid, name) for UI/URL, or null. */
export function normalizeOneCohort(cohort) {
    const list = normalizeCohortIds(cohort ? [cohort] : [])
    return list[0] || null
}

function transformCohortToSingleAnimal(cohort, id) {
    const { ids, uid, aIds, name, detail = {}, ...rest } = cohort
    return { ...rest, id, size: 1, detail: { ...detail, ...(name != null && { name }) } }
}

const toNum = x => (typeof x === 'string' ? Number(x) : x)

export const cohortIdHex = ids => {
    let h = 0x811c9dc5 // FNV-1a 32-bit offset basis
    const sorted = [...ids]
        .map(Number)
        .filter(n => !isNaN(n))
        .sort((a, b) => a - b)

    for (const id of sorted) {
        const s = `${id},` // delimiter avoids ambiguity
        for (let i = 0; i < s.length; i++) {
            h ^= s.charCodeAt(i)
            h = Math.imul(h, 0x01000193) >>> 0 // FNV prime
        }
    }

    return h.toString(16).toUpperCase().padStart(8, '0') // e.g. "3FA91C2E"
}

export const useAppSelector = useSelector
export const useGroups = () => useAppSelector(s => s.livestock2.groups.data ?? [])
export const useGroupSummary = (options = {}) => {
    const groups = useGroups()
    return useMemo(() => groups.map(g => summariseGroupAnimals(g, options)), [groups, options])
}

// Single source of truth: default, type (set=add, map=addCount), and value extraction. getValue for set returns one value; for map returns array of [value, count].
const SUMMARY_FIELD_CONFIG = {
    farmIds: { default: [], type: 'set', getValue: a => a?.farmId },
    species: { default: [], type: 'map', getValue: (a, d, b, size) => [[b?.species ?? d?.species, size]] },
    ageClasses: { default: [], type: 'map', getValue: (a, d, b, size) => [[b?.ageClass, size]] },
    genders: { default: [], type: 'map', getValue: (a, d, b, size) => [[b?.gender, size]] },
    dateOfBirths: {
        default: [],
        type: 'dobDerived',
        key: 'dateOfBirthsArr',
        getValue: (a, d, b) => {
            if (!b?.dateOfBirth) return null
            const ts = new Date(b.dateOfBirth).getTime()
            return Number.isNaN(ts) ? null : ts
        },
    },
    breeds: { default: [], type: 'map', getValue: (a, d, b, size) => [[d?.breed ?? b?.breed, size]] },
    healthStatuses: { default: [], type: 'map', getValue: (a, d, b, size) => [[d?.health?.status || 'Unknown', size]] },
    paddockIds: { default: [], type: 'map', getValue: (a, d, b, size) => [[a?.paddockId, size]] },
    reproductiveStates: { default: [], type: 'map', getValue: (a, d, b, size) => [[d?.production?.reproductiveState, size]] },
    reproductiveAvailabilities: { default: [], type: 'map', getValue: (a, d, b, size) => [[d?.production?.reproductiveAvailability, size]] },
    tagTypes: {
        default: [],
        type: 'map',
        getValue: (a, d, b, size) => (d?.identification?.tagTypes || []).map(tag => [[tag?.type, tag?.color, tag?.label, tag?.location].filter(Boolean).join(' | ') || 'Unknown', size]),
    },
    size: { default: undefined, type: 'passThrough', groupKey: 'size' },
    id: { default: undefined, type: 'passThrough', groupKey: 'id' },
    name: { default: undefined, type: 'passThrough', groupKey: 'name' },
    created: { default: undefined, type: 'passThrough', groupKey: 'created' },
    updated: { default: undefined, type: 'passThrough', groupKey: 'updated' },
    dobRange: { default: [], type: 'dobDerived', key: 'dobRange' },
    dobRangeAll: { default: [], type: 'dobDerived', key: 'dobRangeAll' },
    averageBirthWeight: { default: null, type: 'averageBirthWeight' },
    dobs: {
        default: [],
        type: 'map',
        getValue: (a, d, b, size) => {
            if (!b?.dateOfBirth) return []
            return [[format(b.dateOfBirth, 'yyyy-MM-01'), size]]
        },
    },
}
const SUMMARY_VALID_FIELDS = new Set(Object.keys(SUMMARY_FIELD_CONFIG))

function getDefault(key, group) {
    const c = SUMMARY_FIELD_CONFIG[key]
    return c?.groupKey != null ? group?.[c.groupKey] : c?.default
}

function buildResult(keys, state, mapToCountArray) {
    const out = {}
    for (const key of keys) {
        const c = SUMMARY_FIELD_CONFIG[key]
        if (!c) continue
        if (c.type === 'passThrough') {
            out[key] = c.groupKey === 'size' && state.totalSize != null ? state.totalSize : state.group?.[c.groupKey]
            continue
        }
        if (c.type === 'set') {
            const set = state[key]
            out[key] = set ? [...set] : (c.default ?? [])
            continue
        }
        if (c.type === 'map') {
            const map = state[key]
            out[key] = map ? mapToCountArray(map) : (c.default ?? [])
            continue
        }
        if (c.type === 'averageBirthWeight') {
            const sum = state.birthWeightSum ?? 0
            const count = state.birthWeightCount ?? 0
            out[key] = count > 0 ? sum / count : null
            continue
        }
        if (c.type === 'dobDerived') {
            if (c.key === 'dobRange') out[key] = state.dobRange ?? []
            else if (c.key === 'dobRangeAll') out[key] = state.dateOfBirthsArr ?? []
            else if (c.key === 'dateOfBirthsArr') out[key] = state.dateOfBirthsArr ? [...state.dateOfBirthsArr] : []
            continue
        }
    }
    return out
}

export function summariseGroupAnimals(group, options = {}) {
    const rawFields = options?.fields
    const wantSet = Array.isArray(rawFields) && rawFields.length > 0 ? new Set(rawFields.filter(f => SUMMARY_VALID_FIELDS.has(f))) : null
    const want = key => wantSet === null || wantSet.has(key)
    const requestedKeys = wantSet === null ? Object.keys(SUMMARY_FIELD_CONFIG) : [...wantSet]

    const animals = group?.animals
    if (!Array.isArray(animals) || animals.length === 0) {
        const out = {}
        for (const key of requestedKeys) {
            out[key] = getDefault(key, group)
        }
        return out
    }

    const needDobDerived = want('dateOfBirths') || want('dobRange') || want('dobRangeAll')
    const state = { group }
    for (const key of requestedKeys) {
        const c = SUMMARY_FIELD_CONFIG[key]
        if (!c) continue
        if (c.type === 'set') state[key] = new Set()
        else if (c.type === 'map') state[key] = new Map()
    }
    if (needDobDerived) state.dateOfBirths = new Set()
    if (want('averageBirthWeight')) {
        state.birthWeightSum = 0
        state.birthWeightCount = 0
    }
    state.totalSize = 0

    const add = (set, v) => {
        if (v != null) set.add(v)
    }
    const addCount = (map, v, n = 1) => {
        if (v != null) map.set(v, (map.get(v) ?? 0) + (Number(n) || 0))
    }
    const mapToCountArray = map => Array.from(map.entries(), ([value, count]) => ({ value, count })).sort((a, b) => b.count - a.count || (a.value < b.value ? -1 : a.value > b.value ? 1 : 0))

    for (const a of animals) {
        const d = a?.detail
        const b = d?.birth
        const size = a?.size ?? 1
        state.totalSize += size
        for (const key of requestedKeys) {
            const c = SUMMARY_FIELD_CONFIG[key]
            if (!c?.getValue) continue
            const col = c.type === 'dobDerived' && c.key === 'dateOfBirthsArr' ? state.dateOfBirths : state[key]
            if (!col) continue
            if (c.type === 'set' || (c.type === 'dobDerived' && c.key === 'dateOfBirthsArr')) {
                add(col, c.getValue(a, d, b, size))
            } else if (c.type === 'map') {
                for (const [val, n] of c.getValue(a, d, b, size)) addCount(col, val, n)
            }
        }
        if (want('averageBirthWeight')) {
            const w = b?.birthWeight
            if (typeof w === 'number' && !Number.isNaN(w)) {
                state.birthWeightSum += w * size
                state.birthWeightCount += size
            }
        }
    }

    if (state.dateOfBirths) {
        state.dateOfBirthsArr = [...state.dateOfBirths].sort((a, b) => b - a)?.map(d => formatLivestockAge(d))
        const arr = state.dateOfBirthsArr
        state.dobRange = arr.length > 1 ? [arr[0], arr[arr.length - 1]] : arr.length ? [arr[0]] : []
    }

    return buildResult(requestedKeys, state, mapToCountArray)
}

export const useCohorts = () => useAppSelector(s => s.livestock2.cohorts.data ?? [])

export function useSummariseCohorts(options = {}) {
    const { cohortsOverride, fields } = options
    const allCohorts = useCohorts()
    const cohorts = cohortsOverride !== undefined ? cohortsOverride : allCohorts
    return useMemo(() => summariseGroupAnimals({ animals: cohorts }, { fields }), [cohorts, fields])
}

function groupByField(items, path) {
    if (!Array.isArray(items) || path == null) return {}
    const out = {}
    for (const item of items) {
        if (item == null) continue
        const key = getNestedValue(item, path)
        const k = key === undefined || key === null ? '__unknown__' : String(key)
        if (!out[k]) out[k] = []
        out[k].push(item)
    }
    return out
}

function computeGroupsForCohortBucket(items, groups) {
    if (!Array.isArray(items) || !Array.isArray(groups)) return []
    const bucketIds = new Set(items.flatMap(c => c?.aIds ?? []))
    return groups
        .map(g => {
            const aIds = g?.aIds ?? []
            const size = aIds.filter(id => bucketIds.has(id)).length
            return size > 0 ? { id: g?.id, name: g?.name, size } : null
        })
        .filter(Boolean)
        .sort((a, b) => (b?.size ?? 0) - (a?.size ?? 0))
}

/**
 * Summary by a subset: group source data by `field`, then summarise each bucket with `fields`.
 * e.g. useSummariseBy({ fields: ['ageClasses', 'breeds'], field: 'detail.species', source: 'cohorts' })
 * returns { Cattle: { ageClasses: [...], breeds: [...] }, Sheep: { ... }, ... }
 *
 * Optional `groupsOverride` / `cohortsOverride` replace Redux lists (e.g. session-backed demo groups from the parent).
 */
export function useSummariseBy(options = {}) {
    const { field, fields, source = 'cohorts', groupsOverride, cohortsOverride } = options
    const cohortsFromStore = useCohorts()
    const groupsFromStore = useGroups()
    const cohorts = cohortsOverride !== undefined ? cohortsOverride : cohortsFromStore
    const groupsResolved = groupsOverride !== undefined ? groupsOverride : groupsFromStore
    const data = source === 'groups' ? groupsResolved : cohorts
    return useMemo(() => {
        const buckets = groupByField(data, field)
        const result = {}
        for (const [key, items] of Object.entries(buckets)) {
            const animals = source === 'cohorts' ? items : items.flatMap(g => g?.animals ?? [])
            result[key] = summariseGroupAnimals({ animals }, { fields })
            if (source === 'cohorts' && field === 'detail.species') {
                result[key].groups = computeGroupsForCohortBucket(items, groupsFromStore)
            }
        }
        return result
    }, [data, field, fields, groupsFromStore, source])
}

/**
 * Cached lookup: animal id → list of group names. Uses useGroups() internally.
 * @returns {(animalId: number) => string[]} Lookup by animal id; returns [] or {} when empty.
 * @example
 * const groupLookup = useGroupLookup()
 * groupLookup(cell.getData()?.id)?.join(', ') || 'Unknown'
 */
export function useGroupLookup() {
    const groupCacheRef = useRef(new Map())
    const groups = useGroups()

    useEffect(() => {
        groupCacheRef.current.clear()
    }, [groups])
    return useCallback(
        animalId => {
            if (!animalId || !groups?.length) return {}
            const cache = groupCacheRef.current
            const cached = cache.get(animalId)
            if (cached != null) return cached
            const grps = (groups?.filter(g => g?.aIds?.includes(animalId)) ?? []).map(g => g?.name) ?? []
            if (!grps.length) return []
            cache.set(animalId, grps)
            return grps
        },
        [groups],
    )
}

/**
 * Cached lookup: cohort uid → cohort with paddock name. Uses useCohorts() and usePaddockMap().
 * @returns {(cohortUid: string) => object|null} Cohort (with .paddock) or null.
 * @example
 * const cohortLookup = useCohortLookup()
 * cohortLookup(cell.getData()?.uid)?.detail?.species || 'Unknown'
 */
export function useCohortLookup() {
    const cohortCacheRef = useRef(new Map())
    const cohorts = useCohorts()
    const paddockMap = usePaddockMap()

    useEffect(() => {
        cohortCacheRef.current.clear()
    }, [cohorts, paddockMap])

    return useCallback(
        cohortUid => {
            if (!cohortUid || !paddockMap) return {}
            const cache = cohortCacheRef.current
            const cached = cache.get(cohortUid)
            if (cached != null) return cached
            let cohort = cohorts?.find(c => c.uid === cohortUid) ?? null
            if (!cohort) return null
            const pid = cohort?.paddockId
            if (pid && paddockMap?.[pid]) {
                cohort = { ...cohort, paddock: paddockMap[pid].name || 'Unknown' }
            }
            cache.set(cohortUid, cohort)
            return cohort
        },
        [cohorts, paddockMap],
    )
}

export function useGroupMap({ full = false, id = null } = {}) {
    const groups = useGroups().filter(g => (id ? g.id === id : true))
    return useMemo(() => {
        return groups.reduce((acc, group) => {
            acc[group.id] = full ? { ...group } : { name: group.name }
            return acc
        }, {})
    }, [groups, full])
}

export function useFilteredGroups(criteria, fieldKeys) {
    const groups = useGroups()
    const uniqueId = criteria && fieldKeys ? `${criteria?.toString()}-${fieldKeys?.toString()}` : 'no-criteria-or-field-keys'
    const cachedGroups = useRef(new Map())
    return useMemo(() => {
        if (cachedGroups.current.has(uniqueId)) {
            return cachedGroups.current.get(uniqueId)
        }
        if (!criteria || !fieldKeys) return groups
        const filteredGroups = filterGroups(groups, criteria, fieldKeys)
        cachedGroups.current.set(uniqueId, filteredGroups)
        return filteredGroups.filter(g => !g?.detail?.archived)
        // return criteria && fieldKeys ? filterGroups(groups, criteria, fieldKeys) : groups
    }, [uniqueId, criteria, fieldKeys, groups])
}
// TODO: review this later
export function getGroupMap({ full = false } = {}) {
    const state = store.getState()

    // Safety check: Ensure the slice exists before accessing .data
    const livestock = state.livestock2
    if (!livestock || !livestock.groups) return {}

    const groups = livestock.groups.data || []
    return groups.reduce((acc, group) => {
        acc[group.id] = full ? { ...group } : { name: group.name }
        return acc
    }, {})
}

export function getFilteredGroups(criteria, fieldKeys) {
    const groups = store.getState().livestock2.groups.data || []
    if (!groups || !groups.length) return {}
    return filterGroups(groups, criteria, fieldKeys)
}

// function getNestedValue(obj, path) {
//     return path.split('.').reduce((acc, key) => acc?.[key], obj)
// }

// function compare(left, operator, right) {
//     switch (operator) {
//         case '>':
//             return left > right
//         case '>=':
//             return left >= right
//         case '<':
//             return left < right
//         case '<=':
//             return left <= right
//         case '===':
//             return left === right
//         case '!==':
//             return left !== right
//         default:
//             throw new Error(`Unsupported operator: ${operator}`)
//     }
// }

// function buildCounts(items, field) {
//     const counts = new Map()

//     for (const item of items) {
//         const value = getNestedValue(item, field)
//         if (value !== undefined) {
//             counts.set(value, (counts.get(value) || 0) + 1)
//         }
//     }

//     return counts
// }

// function filterByCountCriteria(items, criteria) {
//     const countsByField = new Map()

//     for (const { field } of criteria) {
//         if (!countsByField.has(field)) {
//             countsByField.set(field, buildCounts(items, field))
//         }
//     }

//     return items.filter(item => {
//         return criteria.every(({ field, operator, value }) => {
//             const fieldValue = getNestedValue(item, field)
//             const counts = countsByField.get(field)
//             const count = counts.get(fieldValue) || 0
//             return compare(count, operator, value)
//         })
//     })
// }
// export const reverseFormatAnimalIds = input => {
//     try {
//         if (input == null) return []
//         if (typeof input === 'number') return Number.isFinite(input) ? [input] : []
//         if (Array.isArray(input)) {
//             return input.flatMap(item => {
//                 if (Array.isArray(item)) {
//                     if (item.length === 2) {
//                         const start = toNum(item[0])
//                         const end = toNum(item[1])
//                         if (!Number.isFinite(start) || !Number.isFinite(end) || start > end) return []
//                         return Array.from({ length: end - start + 1 }, (_, i) => start + i)
//                     }
//                     if (item.length === 1) {
//                         const n = toNum(item[0])
//                         return Number.isFinite(n) ? [n] : []
//                     }
//                     return []
//                 }
//                 const n = toNum(item)
//                 return Number.isFinite(n) ? [n] : []
//             })
//         }
//         if (typeof input === 'object' && input.constructor === Object) {
//             return Object.entries(input)
//                 .filter(([, v]) => v != null && v !== false)
//                 .map(([k]) => Number(k))
//                 .filter(Number.isFinite)
//         }
//         return []
//     } catch {
//         return []
//     }
// }
// export const formatAnimalIds = animals => {
//     if (!Array.isArray(animals) || animals.length === 0) return []

//     // Extract IDs from animal objects or use numbers directly
//     const ids = animals
//         .map(animal => {
//             if (typeof animal === 'number') return animal
//             if (typeof animal === 'string') return Number(animal)
//             return animal?.id != null ? Number(animal.id) : null
//         })
//         .filter(id => id != null && !isNaN(id))
//         .sort((a, b) => a - b)

//     if (ids.length === 0) return []

//     const result = []
//     let rangeStart = ids[0]
//     let rangeEnd = ids[0]

//     for (let i = 1; i < ids.length; i++) {
//         if (ids[i] === rangeEnd + 1) {
//             // Consecutive number, extend the range
//             rangeEnd = ids[i]
//         } else {
//             // Gap found, save current range/single number and start new one
//             if (rangeStart === rangeEnd) {
//                 // Single number
//                 result.push(rangeStart)
//             } else {
//                 // Range
//                 result.push([rangeStart, rangeEnd])
//             }
//             rangeStart = ids[i]
//             rangeEnd = ids[i]
//         }
//     }

//     // Add the last range or single number
//     if (rangeStart === rangeEnd) {
//         result.push(rangeStart)
//     } else {
//         result.push([rangeStart, rangeEnd])
//     }

//     return result
// }
// export const useAppDispatch = () => useDispatch()
